import { useAnalyticsDashboardGridLayout } from "../../../helpers/hooks/useAnalyticsDashboardGridLayout";
import styled from "styled-components";
import { Layout, Responsive, WidthProvider } from "react-grid-layout";
import {
  breakpoints,
  columns,
  ensureBreakpointsHaveAllKeys,
  ensureColsHaveBreakpoints,
  widgetIdSplitSymbol,
} from "../../../helpers/gridLayoutConfigurations";
import WidgetLoader from "./widgetLoader";
import { useSelector } from "react-redux";
import { RootState } from "../../../state/store";
import { useState } from "react";

const safeBreakpoints = ensureBreakpointsHaveAllKeys(breakpoints);
const safeCols = ensureColsHaveBreakpoints(columns);

interface AnalyticsWidgetsGridProps {
  analyticsWidgets: { url: string; type: string }[];
  dashboard: string;
  dashboardWidget: string;
}

const AnalyticsWidgetsGrid = ({
  analyticsWidgets,
  dashboard,
  dashboardWidget,
}: AnalyticsWidgetsGridProps) => {
  const [resizeId, setResizeId] = useState<string | null>(new Date().getTime().toString());
  // Use staff_id from company details for proper scoping
  const companyDetails = useSelector(
    (state: RootState) => state.company.company_details
  );
  const staffId = (companyDetails as any)?.user_id || 0;

  const { gridLayoutConfiguration, saveLayoutToLS } =
    useAnalyticsDashboardGridLayout({
      analyticsWidgets,
      dashboard,
      dashboardWidget,
      staffId,
    });

  const handleOnDragStop = (layout: Layout[]) => {
    try {
      saveLayoutToLS(layout);
    } catch (error) {
      console.error("Error saving analytics layout on drag stop:", error);
    }
  };

  const handleResizeStop = (layout: Layout[]) => {
    setResizeId(new Date().getTime().toString());
    try {
      saveLayoutToLS(layout);
    } catch (error) {
      console.error("Error saving analytics layout on resize stop:", error);
    }
  };

  if (!analyticsWidgets.length || !gridLayoutConfiguration) {
    return null;
  }

  return (
    <GridLayout
      className="grid-layout"
      breakpoints={safeBreakpoints}
      rowHeight={25}
      verticalCompact
      compactType="vertical"
      preventCollision={false}
      allowOverlap={false}
      cols={safeCols}
      layouts={gridLayoutConfiguration || {}}
      containerPadding={{
        xxl: [40, 20],
        xl: [35, 18],
        lg: [30, 15],
        md: [20, 12],
        sm: [15, 10],
        xs: [10, 8],
        xxs: [5, 5],
      }}
      isBounded
      margin={[20, 20]}
      onDragStop={handleOnDragStop}
      onResizeStop={handleResizeStop}
      useCSSTransforms={true}
      isDraggable
      isResizable
      draggableHandle=".drag-handle"
      resizeHandles={["se"]}>
      {analyticsWidgets.map((widget) => {
        // Use consistent widget key generation with widgetIdSplitSymbol
        const widgetKey = `${widget.url}${widgetIdSplitSymbol}${widget.type}`;
        return (
          <div key={widgetKey}>
            <WidgetLoader
              widget={widget}
              dashboard={dashboard}
              dashboardWidget={dashboardWidget}
            />
          </div>
        );
      })}
    </GridLayout>
  );
};

export default AnalyticsWidgetsGrid;

const GridLayout = styled(WidthProvider(Responsive))`
  margin: 20px;
  .react-grid-item.react-grid-placeholder {
    background: rgba(128, 128, 128, 0.3);
    border: 2px dashed #ccc;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .react-grid-item.react-grid-placeholder:hover {
    background: rgba(128, 128, 128, 0.5);
    border-color: #999;
  }

  .react-grid-item {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .react-grid-item.react-draggable-dragging {
    z-index: 1000;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }

  .react-grid-item.react-resizable-resizing {
    z-index: 999;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;
